import { useEffect, useRef } from 'react'
import { z } from 'zod'

import { useAppConfigStore } from '@/stores/app-store-config/appConfigStore'
import { useCartStore } from '@/stores/cart-store/cartStore'
import { CartStoreSlices } from '@/stores/cart-store/slices/types'
import { dateStringToDateSchema } from '@/utils/dateStringToDateSchema'
import { getAdvertisementCode } from '@/utils/getAdvertisementCode'

export const ULR_QUERY_PARAM = 'query'

const priceSchema = z.object({
	amountAfterTax: z.number(),
	amountBeforeTax: z.number().nullish(),
	taxes: z
		.object({
			inclusive: z.boolean(),
			name: z.string(),
			description: z.string(),
			amount: z.number().nullish(),
			percentage: z.number().nullish(),
		})
		.array()
		.nullable(),
})

const roomSuggestionSchema = z.object({
	roomTypeCode: z.string().nullish(),
	ratePlanCode: z.string().nullish(),
	serviceCodes: z.string().array().nullish(),
})

const roomConfigQuerySchema = z
	.object({
		adults: z.number(),
		children: z.number().array().nullish(),
		type: z.string().nullish(),
		roomSuggestion: roomSuggestionSchema.nullish(),
	})
	.array()
	.nullish()

const roomSchema = z.object({
	title: z.string(),
	roomTypeCode: z.string(),
	rate: z.object({
		title: z.string(),
		ratePlanCode: z.string(),
		price: priceSchema,
		cancellationDeadline: z.string().nullish(),
	}),
	services: z
		.object({
			title: z.string(),
			serviceCode: z.string(),
			servicePricing: z.string().nullish(),
			price: priceSchema.nullable(),
			quantity: z.number().nullish(),
			flexibleQuantity: z.boolean().nullish(),
			maxQuantity: z.number().nullish(),
		})
		.array(),
})

export const urlQuerySchema = z.object({
	promotionCode: z.string().nullish(),
	hotelCode: z.string().nullish(),
	startDate: dateStringToDateSchema.nullish(),
	endDate: dateStringToDateSchema.nullish(),
	roomConfigs: roomConfigQuerySchema,
	rooms: roomSchema.array().nullish(),
	reservationId: z.string().nullish(),
	isDeeplink: z.boolean().nullish(),
})

export type RoomConfigQuery = z.infer<typeof roomConfigQuerySchema>
export type UrlQuery = z.infer<typeof urlQuerySchema>

export const mapCartStoreToUrlQuery = (cartStore: CartStoreSlices): UrlQuery => ({
	promotionCode: cartStore.promotion.code,
	hotelCode: cartStore.location?.hotelCode,
	startDate: cartStore.travelPeriod.startDate,
	endDate: cartStore.travelPeriod.endDate,
	roomConfigs: cartStore.roomConfigurations.map((rc) => ({
		adults: rc.adults,
		children: rc.children,
		type: rc.type,
	})),
	rooms: cartStore.roomSelection.selectedRooms.map((sr) => sr.room),
})

const updateAdvertisementCookie = () => {
	const dsrPromotionCode = getAdvertisementCode()

	if (!dsrPromotionCode) return

	const addCookie = (promotionCodeValue: string) => {
		document.cookie = `bstatus=${promotionCodeValue}; Path=/; Max-Age=2592000; SameSite=None; Secure`
	}

	addCookie(dsrPromotionCode)
}

export const useUrlQUery = () => {
	const cartStore = useCartStore()
	const addRoomConfiguration = useCartStore.use.addRoomConfiguration()
	const setUrlQuery = useAppConfigStore.use.setUrlQuery()
	const canSetQuery = useRef<boolean>(false)
	const inBackend = useAppConfigStore.use.inBackend?.()

	if (canSetQuery.current) {
		const url = new URL(window.location.href)
		const searchParams = url.searchParams
		searchParams.set(
			ULR_QUERY_PARAM,
			encodeURIComponent(JSON.stringify(mapCartStoreToUrlQuery(cartStore)))
		)

		if (!inBackend) {
			history.replaceState(null, '', url.toString())
		}
	}

	useEffect(() => {
		if (canSetQuery.current) return

		const urlParams = new URLSearchParams(window.location.search)
		const queryString = urlParams.get(ULR_QUERY_PARAM)
		updateAdvertisementCookie()

		if (!queryString) {
			addRoomConfiguration()
			canSetQuery.current = true
			return
		}

		const query = urlQuerySchema.safeParse(JSON.parse(decodeURIComponent(queryString)))
		if (query.success) {
			setUrlQuery(query.data)
		}
		addRoomConfiguration()
		canSetQuery.current = true
	}, [addRoomConfiguration, setUrlQuery])
}
